import { useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { date, money, percent, shortDate } from '@/lib/format'
import { categoriesOf, filterProducts } from '@/lib/rateCard'
import { useAsync } from '@/lib/useAsync'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Card, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { Input, Select } from '@/components/ui/form'
import type { BillingType, PortalRateCard, PortalRateCardProduct } from '@/types/api'

/**
 * What Codemax sells, and what it costs.
 *
 * The one screen in this portal used *before* a deal exists, which is why it sits second in
 * the navigation and everything to its right is the record of one that already does.
 *
 * It is a reference, not an offer. There is nothing here to edit, nothing to add to
 * anything, and nothing to export - the last on purpose: an exported price list in an inbox
 * is a copy of something that may since have changed, and the two disagreeing is worse than
 * only having one. The live page is the artefact, the same reasoning that keeps a quotation
 * out of an email attachment.
 *
 * Prices are the ones in force on the API's own date, resolved by the same function the
 * quotation builder calls when it inserts a line. A partner reading 480 here and a rep
 * inserting that line an hour later cannot get two different numbers.
 */
export function RateCard() {
  const card = useAsync<PortalRateCard>(
    (signal) => api.get<PortalRateCard>('/portal/rate-card', undefined, signal),
    [],
  )

  const [countryIso, setCountryIso] = useState<string | undefined>(undefined)
  const [term, setTerm] = useState('')
  const [category, setCategory] = useState('')

  const countries = card.data?.countries ?? []

  // The first country until somebody picks another. Held as an ISO code rather than an index
  // so a reload that returns a different set does not silently select a different country.
  const country = countries.find((c) => c.isoCode === countryIso) ?? countries[0]

  const categories = useMemo(() => categoriesOf(country), [country])

  const products = useMemo(
    () => filterProducts(country, term, category),
    [country, term, category],
  )

  if (card.loading && !card.data) return <Loading />
  if (card.error) {
    return <ErrorState title={card.error.title} detail={card.error.detail} onRetry={card.reload} />
  }

  return (
    <>
      <PageHeader
        title="Rate Card"
        hint="Indicative list prices, in force today. A quotation fixes its prices when it is raised, and any discount is Codemax's to approve."
      />

      {!country ? (
        <Card>
          <EmptyState
            title="No price list yet"
            hint="We have not set your countries up for quoting. Speak to your Codemax contact and this page will fill itself in."
          />
        </Card>
      ) : (
        <>
          {/* Only when there are two. The shell has no country switcher on purpose - a
              partner is one company - and this is the one screen where that stops being
              true, because the same product has a Malaysian price and a Singapore price and
              they are different numbers in different currencies. */}
          {countries.length > 1 && (
            <Tabs
              tabs={countries.map((c) => ({ id: c.isoCode, label: c.name }))}
              active={country.isoCode}
              onSelect={setCountryIso}
            />
          )}

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Input
              className="w-full sm:w-64"
              type="search"
              value={term}
              placeholder="Search products"
              aria-label="Search products"
              onChange={(e) => setTerm(e.target.value)}
            />

            {/* Only when there is something to filter. One category in a dropdown of one is
                a control that cannot change anything. */}
            {categories.length > 1 && (
              <Select
                className="w-full sm:w-52"
                value={category}
                aria-label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}

            <p className="ml-auto font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
              {country.currencyCode} &middot; in force {date(card.data?.asOf)}
            </p>
          </div>

          {products.length === 0 ? (
            <Card>
              <EmptyState
                title={term || category ? 'Nothing matches that' : 'Nothing priced here yet'}
                hint={
                  term || category
                    ? 'Clear the search or the category to see the whole list.'
                    : 'No product carries a price in this country today. Speak to your Codemax contact.'
                }
              />
            </Card>
          ) : (
            <div className="flex flex-col gap-4">
              {products.map((product) => (
                <Solution key={product.sku} product={product} currencyCode={country.currencyCode} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  )
}

/**
 * One solution, shaped the way the customer's own document shapes it.
 *
 * A quotation is solution blocks rather than flat lines, and a partner reading a flat SKU
 * table would learn the wrong shape of the product before ever seeing one.
 */
function Solution({
  product,
  currencyCode,
}: {
  product: PortalRateCardProduct
  currencyCode: string
}) {
  // One line under the ladder rather than a column, because it applies to the annual rows
  // only. It is on the card at all because it prints on the quotation the customer receives.
  const annual = product.lines.find((line) => line.annualDiscountPercent > 0)

  return (
    <Card>
      <div className="border-b border-line-soft px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h2 className="text-base font-semibold">{product.tradeName ?? product.name}</h2>
          <Badge tone="accent">{product.sku}</Badge>
          {product.category && <span className="text-[12px] text-faint">{product.category}</span>}
        </div>

        {product.summaryLine && (
          <p className="mt-1 text-[13px] text-ink-2">{product.summaryLine}</p>
        )}

        {product.modules.length > 0 && (
          <ul className="mt-2.5 flex flex-wrap gap-1.5">
            {product.modules.map((module) => (
              <li
                key={module}
                className="rounded-full bg-sunken px-2.5 py-0.5 text-[11px] text-muted"
              >
                {module}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Item</Th>
            <Th>Billing</Th>
            <Th>Per</Th>
            <Th numeric>Min qty</Th>
            <Th numeric>Unit price ({currencyCode})</Th>
            <Th numeric>In force since</Th>
          </tr>
        </thead>
        <tbody>
          {product.lines.map((line) => (
            <Tr key={line.name}>
              <Td className="font-medium">{line.name}</Td>
              <Td>
                <BillingBadge billing={line.billingType} />
              </Td>
              <Td className="text-muted">{line.uom}</Td>
              <Td numeric>{line.minQuantity}</Td>
              <Td numeric>{money(line.unitPrice, currencyCode)}</Td>
              <Td numeric className="text-[12px] text-faint">
                {shortDate(line.effectiveFrom)}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {annual && (
        <p className="px-5 py-3 text-[12px] text-muted">
          Annual billing carries a standard {percent(annual.annualDiscountPercent)} reduction,
          applied on the quotation.
        </p>
      )}
    </Card>
  )
}

function BillingBadge({ billing }: { billing: BillingType }) {
  if (billing === 'Monthly') return <Badge tone="accent">Monthly</Badge>
  if (billing === 'Annual') return <Badge tone="positive">Annual</Badge>

  return <Badge>One-time</Badge>
}
